import React, { useEffect } from 'react';
import {
  customerHoldTypesLoader,
  customerLoader,
  eligibilityOptionalLoader,
  financingRequestLoader,
} from '../../actions/data_loaders';
import { useSelector } from 'react-redux';
import withDataLoaded from '../with_data_loaded';
import DisplayHelper from '../../helpers/display_helper';
import auth_helper from '../../helpers/auth_helper';
import AuthHelper from '../../helpers/auth_helper';
import CustomerHelper from '../../helpers/customer_helper';
import FinancingHelper from '../../helpers/financing_helper';
import moment from 'moment-timezone';
import StepsHelper from '../../helpers/financing_request/steps_helper';
import Actions from '../../actions/actions';

const CustomerStatusBanner = () => {
  const user = useSelector((redux) => redux?.user);
  const eligibility = useSelector((redux) => redux?.eligibility);
  const financingRequest = useSelector((redux) => redux?.financingRequest);
  const isCpf = auth_helper.isCPFUser(user);
  const customer = useSelector((redux) => redux?.customer);
  const financingMethod = useSelector((redux) => redux?.financingMethod);
  const customerBannerData = useSelector((redux) => redux?.customerBannerData);
  const customerHoldTypes = useSelector((redux) => redux?.customerHoldTypes);

  useEffect(() => {
    Actions.fetchCustomerBannerData({ customerId: customer?.id, status: customer?.status });
  }, [customer?.id, customer?.status, customer?.holdCategorySku, user]);

  const CancellationBanner = () => {
    const relatedFrid = customerBannerData.filter(
      (entry) => entry.tableName === 'financing_requests' && entry.to === 'cancelled'
    );
    const relatedCancellation = relatedFrid?.[0];
    const hasRelatedData =
      relatedCancellation?.userFullName && relatedCancellation?.username && relatedCancellation?.changeDate;

    const fridDateDeletedDate = DisplayHelper.formatDateNoTime(relatedCancellation?.changeDate);
    const fridDateDeletedTime = DisplayHelper.formatDateTime(relatedCancellation?.changeDate);

    const BaseCancellationBanner = () => {
      return `Customer was cancelled`;
    };

    const DetailedCancellationBanner = () => {
      return (
        <div className="customer-banner-customer-information">
          {`Job Cancelled - `}
          <span id="qa-cancelled-banner-date">{fridDateDeletedDate}</span>
          {` `}
          <span id="qa-cancelled-banner-time">{fridDateDeletedTime}</span>
          {` by `}
          <span id="qa-cancelled-banner-user">
            {relatedCancellation?.isCpf && !AuthHelper.isCPFUser(user)
              ? `Concert Support Admin`
              : `${relatedCancellation?.userFullName} (${relatedCancellation?.username})`}
          </span>
        </div>
      );
    };

    return (
      <>
        {CustomerHelper.isCancelled(customer) && (
          <div id="qa-banner-cancelled" className="customer-banner-cancelled">
            {!relatedCancellation || !hasRelatedData ? BaseCancellationBanner() : DetailedCancellationBanner()}
            {isCpf && relatedCancellation?.reason && (
              <div className="customer-banner-cancellation-reason">
                {`Reason - `}"<span id="qa-cancelled-reason">{relatedCancellation?.reason}</span>"
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  const HoldBanner = () => {
    const relatedHoldEntries = customerBannerData.filter((entry) => {
      return entry.tableName === 'quoting_customers' && entry.columnName === 'hold_category_sku';
    });
    const relatedHold = relatedHoldEntries?.[0];
    const changeDate = DisplayHelper.formatDateV2(relatedHold?.changeDate);
    const holdType = customerHoldTypes?.find((type) => type.sku === customer?.holdCategorySku);

    const BaseHoldBanner = () => {
      return `Customer on hold as of ${changeDate}`;
    };

    const HoldReason = () => {
      return (
        <div className="customer-banner-hold-reason">
          {`Reason - `}"<span id="qa-hold-reason">{isCpf ? holdType?.label : holdType?.externalLabel}</span>"
        </div>
      );
    };

    const AdditionalDetails = () => {
      return (
        <>
          {relatedHold?.reason && (
            <div className="customer-banner-hold-additional-details">
              {`Additional Details - `}"<span id="qa-hold-additional-details">{relatedHold?.reason}</span>"
            </div>
          )}
        </>
      );
    };

    return (
      <>
        {!CustomerHelper.isCancelled(customer) && CustomerHelper.isOnHold(customer) && (
          <div id="qa-banner-hold" className="customer-banner-hold">
            <BaseHoldBanner />
            <HoldReason />
            {isCpf && <AdditionalDetails />}
          </div>
        )}
      </>
    );
  };

  const ArchivedBanner = () => {
    return (
      <>
        {customer?.status === 'Archived' && (
          <div id="qa-banner-archived" className="customer-banner-archived">
            Customer is Archived
          </div>
        )}
      </>
    );
  };

  const RetiredRateCardBanner = () => {
    return (
      <>
        {FinancingHelper.isOnRetiredRateCard(financingMethod) && (
          <div id="qa-banner-retired" className="customer-banner-retired-rate-card">
            {
              'The previously selected product for this customer has been retired. Please select a new product to continue.'
            }
          </div>
        )}
      </>
    );
  };

  const ExpireBanner = () => {
    let daysLeftUntilExpire = moment(eligibility?.creditCheck?.expireDate)
      .utc()
      .diff(moment().startOf('day').utc(), 'days');

    return (
      <>
        {eligibility?.creditCheck?.expireDate &&
          daysLeftUntilExpire <= 14 &&
          !StepsHelper.isPastSubstantialCompletion(financingRequest) && (
            <div id="qa-banner-expiring" className="customer-banner-expiring">
              Step expire{daysLeftUntilExpire >= 0 ? 's' : 'd'} on{' '}
              {DisplayHelper.formatLocalDateNoTime(eligibility?.creditCheck?.expireDate)}
            </div>
          )}
      </>
    );
  };

  const M1FreezeBanner = () => {
    if (customer?.owner?.company?.elements?.some(({ elementTypeSku }) => elementTypeSku === 'M1_FREEZE_FLAG_LOAN')) {
      let reason = customer?.owner?.company?.elements.find(
        ({ elementTypeSku }) => elementTypeSku === 'M1_FREEZE_REASON_LOAN'
      );

      let freezeFlag = customer?.owner?.company?.elements?.find(
        ({ elementTypeSku }) => elementTypeSku === 'M1_FREEZE_FLAG_LOAN'
      );

      return (
        <div id="qa-banner-installer-m1-freeze" className="customer-installer-m1-freeze">
          Company is on M1 Freeze as of {DisplayHelper.formatDateNoTime(freezeFlag?.dateCreated)} (reason:{' '}
          {reason?.elementValue}; placed by {reason?.createdBy?.firstname} {reason?.createdBy?.lastname})
        </div>
      );
    }
    return <></>;
  };

  return (
    <>
      {customer && customerBannerData && (
        <>
          <CancellationBanner />
          <HoldBanner />
          <ArchivedBanner />
          <RetiredRateCardBanner />
        </>
      )}
      <ExpireBanner />
      {customer && customerBannerData && <M1FreezeBanner />}
    </>
  );
};

const ConnectedCustomerBannerWithData = withDataLoaded({
  WithData: CustomerStatusBanner,
  data: [
    customerLoader,
    eligibilityOptionalLoader,
    financingRequestLoader,
    // customerBannerDataLoader,
    customerHoldTypesLoader,
  ],
});

export default ConnectedCustomerBannerWithData;
